import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { KpiCard, PageHeader, pct, num } from "../lib/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FileCheck2, Percent, Siren, Microscope, Users, Building2 } from "lucide-react";
import { ZoneMap } from "../components/ZoneMap";

export default function HealthZones() {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const allZones = useMemo(() => {
    const geo = cfg?.geo || {};
    return Object.entries(geo).flatMap(([p, pv]) =>
      Object.values(pv.territories || {}).flat().map((z) => ({ zone: z, province: p })));
  }, [cfg]);
  const [zone, setZone] = useState("");
  React.useEffect(() => { if (!zone && allZones.length) setZone(allZones[0].zone); }, [allZones, zone]);

  const { data } = useQuery({
    queryKey: ["hz", zone],
    queryFn: async () => (await api.get(`/profiles/health-zone/${encodeURIComponent(zone)}`)).data,
    enabled: !!zone,
  });
  const m = data?.metrics || {};

  return (
    <div data-testid="health-zones-page">
      <PageHeader title={t("Fiche Zone de Santé")} subtitle={t("Profil épidémiologique détaillé par zone")}>
        <Select value={zone} onValueChange={setZone}>
          <SelectTrigger className="w-56" data-testid="hz-select"><SelectValue placeholder={t("Choisir une zone")} /></SelectTrigger>
          <SelectContent className="max-h-72">{allZones.map((z) => <SelectItem key={z.zone} value={z.zone}>{z.zone} ({z.province})</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {data?.empty ? <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-400">{t("Aucune donnée pour cette zone.")}</div> : data && (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-5 mb-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div><span className="text-xs text-slate-500">Province</span><p className="font-semibold">{data.province}</p></div>
              <div><span className="text-xs text-slate-500">Épidémiologistes</span><p className="font-semibold">{data.epidemiologists?.join(", ")}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard label="Rapports reçus" value={num(data.reports_received)} icon={FileCheck2} />
            <KpiCard label="Complétude" value={pct(data.completeness)} icon={Percent} tone="teal" />
            <KpiCard label="Alertes reçues" value={num(m.alerts_received)} icon={Siren} tone="amber" />
            <KpiCard label="Cas suspects" value={num(m.suspected_cases)} icon={Microscope} tone="red" />
            <KpiCard label="Contacts suivis" value={num(m.contacts_followed)} icon={Users} />
            <KpiCard label="FOSA visitées" value={num(m.facilities_visited)} icon={Building2} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <ZoneMap province={data.province} highlight={zone} height={260} testid="hz-map" />
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Historique hebdomadaire</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.weekly_history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="reports" name={t("Rapports")} stroke="#F05A28" strokeWidth={2} />
                  <Line type="monotone" dataKey="alerts_received" name={t("Alertes")} stroke="#D97706" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Principaux défis</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.top_challenges.slice(0, 6)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                  <Tooltip /><Bar dataKey="value" name={t("Signalements")} fill="#DC2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mt-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Recommandations en attente</h3>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Recommandation</TableHead><TableHead>Priorité</TableHead><TableHead>Statut</TableHead><TableHead>Échéance</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.pending_recommendations?.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Aucune.</TableCell></TableRow> :
                  data.pending_recommendations?.map((r) => <TableRow key={r.id}><TableCell className="text-sm">{r.recommendation}</TableCell><TableCell className="text-sm">{r.priority}</TableCell><TableCell className="text-sm">{r.status}</TableCell><TableCell className="font-mono text-xs">{r.due_date || "-"}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
